"use client";

import { useState } from "react";
import { Offers } from "@pamperedchef/design-system";
import type { CatalogLabel } from "@/domain/catalog";
import * as styles from "./ProductOfferLabel.module.scss";

const OFFERS_ICON_ALIAS: Record<string, string> = {
	truck: "delivery-color"
};

function toOffersIcon(name?: string): { name: string } | undefined {
	if (!name) return undefined;
	return { name: OFFERS_ICON_ALIAS[name] ?? name };
}

export interface ProductOfferLabelProps {
	/**
	 * `kind: "product_offer"` labels from `CatalogProduct.labels` +
	 * `scheduledLabels` (date-window filtered upstream). Callers filter
	 * by kind; this composite stays presentational.
	 */
	labels: CatalogLabel[];
	/**
	 * When more than `collapseAfter` entries are passed, the overflow
	 * collapses behind a "+N More Deals" toggle. Default: 2.
	 */
	collapseAfter?: number;
	className?: string;
}

/**
 * Stacks the product's free-shipping / host-party / discount messages below
 * the price block. Three offers or more → first two shown, remainder behind
 * a "+N More Deals" expand affordance.
 */
export function ProductOfferLabel({
	labels,
	collapseAfter = 2,
	className
}: ProductOfferLabelProps) {
	const [expanded, setExpanded] = useState(false);

	if (!labels.length) return null;

	const collapsible = labels.length > collapseAfter;
	const visible =
		!collapsible || expanded ? labels : labels.slice(0, collapseAfter);
	const hiddenCount = labels.length - collapseAfter;

	return (
		<div
			className={[styles["offer-label"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductOfferLabel"
		>
			{visible.map((label, idx) => (
				<div
					key={`${label.label}-${idx}`}
					className={styles["offer-label__item"]}
				>
					<Offers
						surface="default"
						text={label.label}
						icon={toOffersIcon(label.icon)}
					/>
				</div>
			))}
			{collapsible && !expanded ? (
				<button
					type="button"
					className={styles["offer-label__toggle"]}
					onClick={() => setExpanded(true)}
				>
					{`+${hiddenCount} More Deals`}
				</button>
			) : null}
		</div>
	);
}
